import {
  collection,
  getCountFromServer,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  Timestamp,
  where,
} from "firebase/firestore";
import type { QueryDocumentSnapshot } from "firebase/firestore";
import { database } from "../..";
import { handleAuthError, isAuthError } from "../../../utils/handleAuthError";

// "propio": puntos que el cliente acumula por sus propios paquetes.
export type NivelReferido = "directo" | "indirecto" | "propio";

/**
 * Evento de acumulación de puntos. Lo escribe la Cloud Function
 * `acumularPuntosReferido` cuando un paquete entra a un estado de entrega:
 * para los referidores del cliente (directo/indirecto) y para el propio
 * cliente por su paquete (propio).
 */
export interface Referido {
  id: string;
  paqueteId: string;
  idRastreo: string;
  // Cliente cuyo paquete generó los puntos.
  clienteId: string;
  clienteNombre: string;
  // Usuario que recibe los puntos (referidor directo/indirecto, o el cliente).
  beneficiarioId: string;
  beneficiarioNombre: string;
  nivel: NivelReferido;
  porcentaje: number;
  base: number;
  puntos: number;
  moneda?: string | null;
  createdAt?: Timestamp;
}

const REFERIDOS_PAGE_SIZE = 100;

export async function fetchReferidosLoteado(
  startDate: Date,
  endDate: Date,
  lastDoc?: QueryDocumentSnapshot
): Promise<{ referidos: Referido[]; lastDoc?: QueryDocumentSnapshot }> {
  try {
    const referidosRef = collection(database, "referidos");

    const referidosQuery = query(
      referidosRef,
      where("createdAt", ">=", Timestamp.fromDate(startDate)),
      where("createdAt", "<=", Timestamp.fromDate(endDate)),
      orderBy("createdAt", "desc"),
      ...(lastDoc ? [startAfter(lastDoc)] : []),
      limit(REFERIDOS_PAGE_SIZE)
    );

    const snapshot = await getDocs(referidosQuery);

    const referidos = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...(doc.data() as Omit<Referido, "id">),
    }));

    const lastVisible = snapshot.docs[snapshot.docs.length - 1];

    return { referidos, lastDoc: lastVisible };
  } catch (error) {
    if (isAuthError(error)) {
      handleAuthError();
      return { referidos: [], lastDoc: undefined };
    }
    console.error("Error obteniendo referidos: ", error);
    return { referidos: [], lastDoc: undefined };
  }
}

export async function countReferidos(
  startDate: Date,
  endDate: Date
): Promise<number> {
  try {
    const referidosRef = collection(database, "referidos");
    const q = query(
      referidosRef,
      where("createdAt", ">=", Timestamp.fromDate(startDate)),
      where("createdAt", "<=", Timestamp.fromDate(endDate))
    );
    const snapshot = await getCountFromServer(q);
    return snapshot.data().count;
  } catch (error) {
    if (isAuthError(error)) {
      handleAuthError();
      return 0;
    }
    console.error("Error contando referidos: ", error);
    return 0;
  }
}
